import type { RouteLocationRaw, RouteRecordNormalized } from 'vue-router'

const LOCALE_SEPARATOR = '___'

export const useRouteLink = () => {
  const router = useRouter()
  const localePath = useLocalePath()
  const routeMap = computed(() => {
    const map = new Map<string, RouteRecordNormalized[]>()

    for (const route of router.getRoutes()) {

      if (!route.name) {
        continue
      }

      const fullName = route.name.toString()
      const separatorIndex = fullName.indexOf(LOCALE_SEPARATOR)
      const baseName = separatorIndex === -1 ? fullName : fullName.slice(0, separatorIndex)
      const routes = (map.get(baseName) ?? []) as RouteRecordNormalized[]
      routes.push(route)
      map.set(baseName, routes)
    }

    return map
  })

  const normalizePageName = (page: string): string => {
    const segments = page
      .split('/')
      .filter(Boolean)
      .filter(segment => segment !== 'index')

    const normalizedSegments = segments.map(
      segment => {
        if (segment.startsWith('[') && segment.endsWith(']')) {
          return segment.slice(1, -1)
        }
        if (
          segment.startsWith('[[') &&
          segment.endsWith(']]')
        ) {
          return segment.slice(2, -2)
        }

        return segment
      },
    )
    return normalizedSegments.join('-') || page
  }

  const findRoute = (page: string): RouteRecordNormalized | undefined => {

    const baseName = normalizePageName(page)
    const routes = routeMap.value.get(baseName)

    if (!routes?.length) {
      return undefined
    }

    return routes[0]
  }

  const findRouteName = (page: string): string | undefined => {
    const route = findRoute(page)

    if (!route?.name) {
      return undefined
    }

    const name = route.name.toString()
    const separatorIndex = name.lastIndexOf(LOCALE_SEPARATOR)

    if (separatorIndex !== -1) {
      return name.slice(0, separatorIndex)
    }

    return name
  }

  const routeLink = (
    page: string,
    params?: Record<string, string | number | (string | number)[]>,
    linkToLocale?: 'pl' | 'en',
  ): string => {
    const name = findRouteName(page)

    if (!name) {
      console.warn(`[useRouteLink] Route not found for page "${page}".`)
      return ''
    }

    const route: RouteLocationRaw = {
      name,
      ...(params ? { params } : {}),
    }

    if (linkToLocale) {
      return localePath(route, linkToLocale)
    }

    return localePath(route)
  }

  return {
    routeLink,
    findRouteName,
    findRoute,
  }
}
